import type { OrganizationIdInput } from '@/lib/organization-scoping'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import {
  associateSnapdragonWorkstation,
  getSnapdragonWorkstation,
  saveSnapdragonWorkstation,
  type SnapdragonOrganizationScope,
} from '@/lib/services/snapdragon/snapdragon.api'
import queryClient from '@/lib/queryClient'
import {
  invalidateSnapdragonWorkstations,
  snapdragonQueryKeys,
} from '@/lib/services/snapdragon/snapdragon.query'
import type {
  SnapdragonOrderSummaryOutputSettings,
  SnapdragonPosPrintMode,
  SnapdragonPrintingSettings,
  SnapdragonReceiptOutputSettings,
  SnapdragonReceiptProtocol,
  SnapdragonSuperTicketOutputSettings,
  SnapdragonWorkstation,
  SnapdragonWorkstationResponse,
} from '@/lib/services/snapdragon/snapdragon.types'
import { readProperty } from '@/lib/utils'

/** The calls that reach the API need an organization; browser storage keys accept any id. */
type RequiredOrganizationId = SnapdragonOrganizationScope['organizationId']

/** What a browser keeps after pairing with the Snapdragon desktop app. */
export interface LocalPrintPairing {
  token: string
  computerId: string
  machineId: string
  stationId: string
  stationName: string
  workstationId: number | null
  workstationClaim: string
}

/** The receipt settings a profile carries once `normalizeLocalReceiptSettings()` has read them. */
export interface LocalPrintReceiptSettings {
  printerId: string
  protocol: SnapdragonReceiptProtocol
  paperWidthMm: 58 | 80
  cut: boolean
  autoPrint: boolean
}

/** The browser's pairing merged with its workstation's saved settings. */
export interface LocalPrintProfile extends LocalPrintPairing {
  workstationName: string
  locationId: number | null
  registerLabel: string
  setupCompleted: boolean
  posPrintMode: SnapdragonPosPrintMode
  /** Set once the workstation has been read; saving printing settings sends it back. */
  settingsVersion?: string
  orderSummary: SnapdragonOrderSummaryOutputSettings
  superTicket: SnapdragonSuperTicketOutputSettings
  receipt: LocalPrintReceiptSettings
}

export type LocalPrintOutputKind = 'orderSummary' | 'superTicket' | 'receipt'

export interface LocalPrintProfileUpdate {
  type: 'saved' | 'cleared'
  source: 'document' | 'storage' | 'server'
}

/** The station the desktop app reports; its id wins over the one saved in the profile. */
interface LocalPrintStationIdentity {
  stationId?: string | null
  stationName?: string | null
}

/** Workstation fields a save changes; only the keys present are sent. */
export interface LocalPrintWorkstationAssignments {
  name?: string | null
  locationId?: number | null
  registerLabel?: string | null
  setupCompleted?: boolean
  posPrintMode?: SnapdragonPosPrintMode
  includePrintingSettings?: boolean
}

export const localPrintProfileUpdatedEvent = 'snapdragon:local-print-profile-updated'
const localPrintProfileWriteGenerations = new Map<string, number>()
const localPrintProfileRevisions = new Map<string, number>()
const localPrintWorkstationSaveQueues = new Map<string, Promise<unknown>>()

const legacyStorageKey = (organizationId: OrganizationIdInput) =>
  `snapdragon.local-print.v1.${organizationId}`
const activeComputerStorageKey = (organizationId: OrganizationIdInput) =>
  `snapdragon.local-print.active-computer.v1.${organizationId}`
const writeGenerationStorageKey = (organizationId: OrganizationIdInput) =>
  `snapdragon.local-print.write-generation.v1.${organizationId}`
const profileInvalidationStorageKey = (organizationId: OrganizationIdInput) =>
  `snapdragon.local-print.invalidation.v1.${organizationId}`
const computerStorageKey = (organizationId: OrganizationIdInput, computerId: string) =>
  `snapdragon.local-print.v1.${organizationId}.${computerId}`
const activeComputerId = (organizationId: OrganizationIdInput) =>
  window.localStorage.getItem(activeComputerStorageKey(organizationId)) || ''
const currentWriteGeneration = (organizationId: OrganizationIdInput) =>
  localPrintProfileWriteGenerations.get(String(organizationId)) ?? 0
const currentProfileRevision = (organizationId: OrganizationIdInput) =>
  localPrintProfileRevisions.get(String(organizationId)) ?? 0
const storedWriteGeneration = (organizationId: OrganizationIdInput) => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(writeGenerationStorageKey(organizationId)) ?? ''
  } catch {
    return null
  }
}

export function subscribeToLocalPrintProfileUpdates(
  organizationId: OrganizationIdInput,
  listener: (update: LocalPrintProfileUpdate) => void
): () => void {
  if (typeof window === 'undefined') return () => {}

  const resolvedOrganizationId = String(organizationId)
  const resolvedInvalidationStorageKey = profileInvalidationStorageKey(resolvedOrganizationId)
  const handleProfileUpdate = (event: Event) => {
    const detail = readProperty(event, 'detail')
    const detailOrganizationId = readProperty(detail, 'organizationId')
    const isOtherOrganization =
      typeof detailOrganizationId === 'string' || typeof detailOrganizationId === 'number'
        ? String(detailOrganizationId) !== resolvedOrganizationId
        : Boolean(detailOrganizationId)
    if (isOtherOrganization) return
    listener({
      type: readProperty(detail, 'type') === 'cleared' ? 'cleared' : 'saved',
      source: 'document',
    })
  }
  const handleStorageUpdate = (event: StorageEvent) => {
    if (event.key === null) {
      listener({ type: 'cleared', source: 'storage' })
      return
    }
    if (event.key !== resolvedInvalidationStorageKey) return
    listener({ type: 'cleared', source: 'storage' })
  }

  window.addEventListener(localPrintProfileUpdatedEvent, handleProfileUpdate)
  window.addEventListener('storage', handleStorageUpdate)
  const unsubscribeFromWorkstation = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') return
    // Workstation queries always carry an organization, so none can match without one.
    if (organizationId === null || organizationId === undefined) return
    const profile = loadLocalPrintProfile(organizationId)
    if (!profile.token || !profile.computerId) return
    const query = queryClient.getQueryCache().find({
      queryKey: workstationQueryKey(organizationId, profile),
      exact: true,
    })
    if (event.query === query) listener({ type: 'saved', source: 'server' })
  })
  return () => {
    window.removeEventListener(localPrintProfileUpdatedEvent, handleProfileUpdate)
    window.removeEventListener('storage', handleStorageUpdate)
    unsubscribeFromWorkstation()
  }
}

function publishLocalPrintProfileUpdate(
  organizationId: OrganizationIdInput,
  type: LocalPrintProfileUpdate['type']
) {
  const detail = { organizationId: String(organizationId), type }
  if (type === 'cleared') {
    try {
      const nonce =
        globalThis.crypto?.randomUUID?.() ?? `${Date.now()}:${Math.random().toString(36).slice(2)}`
      window.localStorage.setItem(profileInvalidationStorageKey(organizationId), nonce)
    } catch {
      // Same-document consumers still receive the custom event below.
    }
  }
  window.dispatchEvent(new CustomEvent(localPrintProfileUpdatedEvent, { detail }))
}

export function createLocalPrintProfileWriteGuard(organizationId: OrganizationIdInput) {
  const generation = currentWriteGeneration(organizationId)
  const sharedGeneration = storedWriteGeneration(organizationId)
  return () =>
    generation === currentWriteGeneration(organizationId) &&
    sharedGeneration !== null &&
    sharedGeneration === storedWriteGeneration(organizationId)
}

export function createLocalPrintProfileRevisionGuard(
  organizationId: OrganizationIdInput,
  revision = currentProfileRevision(organizationId)
) {
  return () => revision === currentProfileRevision(organizationId)
}

export function advanceLocalPrintProfileRevision(organizationId: OrganizationIdInput): number {
  const nextRevision = currentProfileRevision(organizationId) + 1
  localPrintProfileRevisions.set(String(organizationId), nextRevision)
  return nextRevision
}

export function isLocalPrintProfileWriteInvalidated(error: unknown): boolean {
  return readProperty(error, 'code') === 'local_print_profile_write_invalidated'
}

function invalidateLocalPrintProfileWrites(organizationId: OrganizationIdInput) {
  advanceLocalPrintProfileRevision(organizationId)
  localPrintProfileWriteGenerations.set(
    String(organizationId),
    currentWriteGeneration(organizationId) + 1
  )
  try {
    const nextGeneration =
      globalThis.crypto?.randomUUID?.() ?? `${Date.now()}:${Math.random().toString(36).slice(2)}`
    window.localStorage.setItem(writeGenerationStorageKey(organizationId), nextGeneration)
  } catch {
    // The caller reports that browser data could not be cleared.
  }
}

export function serializeLocalPrintWorkstationMutation<TResult>(
  organizationId: OrganizationIdInput,
  computerId: string,
  mutation: () => Promise<TResult>
): Promise<TResult> {
  const key = `${organizationId}:${computerId}`
  const previous = localPrintWorkstationSaveQueues.get(key) ?? Promise.resolve()
  const queued = previous.catch(() => undefined).then(mutation)
  localPrintWorkstationSaveQueues.set(key, queued)

  return queued.finally(() => {
    if (localPrintWorkstationSaveQueues.get(key) === queued) {
      localPrintWorkstationSaveQueues.delete(key)
    }
  })
}

function profileWriteInvalidatedError() {
  return Object.assign(new Error('The Direct Printing session changed before the save finished.'), {
    code: 'local_print_profile_write_invalidated',
  })
}

function localPrintProfilePairingIsActive(
  organizationId: OrganizationIdInput,
  profile: Partial<Pick<LocalPrintPairing, 'computerId' | 'token'>> | null | undefined
) {
  const computerId = String(profile?.computerId ?? '').trim()
  const token = String(profile?.token ?? '').trim()
  if (!computerId || !token) return false

  try {
    const stored = loadLocalPrintProfile(organizationId, computerId)
    return activeComputerId(organizationId) === computerId && stored.token === token
  } catch {
    return false
  }
}

export function localPrintPendingStorageKey(organizationId: OrganizationIdInput): string {
  const computerId = activeComputerId(organizationId)
  return computerId
    ? `snapdragon.local-print.pending.v1.${organizationId}.${computerId}`
    : `snapdragon.local-print.pending.v1.${organizationId}`
}

export const defaultLocalPrintProfile: LocalPrintProfile = {
  token: '',
  computerId: '',
  machineId: '',
  stationId: '',
  stationName: '',
  workstationId: null,
  workstationClaim: '',
  workstationName: '',
  locationId: null,
  registerLabel: '',
  setupCompleted: false,
  posPrintMode: 'legacy_cloud',
  orderSummary: { enabled: true, printerId: '', copies: 1, tray: '', autoPrint: false },
  superTicket: {
    enabled: true,
    printerId: '',
    copies: 1,
    tray: '',
    template: 'jhc_fb7373_super_ticket',
    paperHasPreprintedBranding: false,
    autoPrint: false,
  },
  receipt: {
    printerId: '',
    protocol: 'escpos',
    paperWidthMm: 80,
    cut: true,
    autoPrint: false,
  },
}

export function normalizeLocalReceiptSettings(
  receipt: Partial<SnapdragonReceiptOutputSettings> | undefined
): LocalPrintReceiptSettings {
  const settings = receipt ?? {}
  const paperWidthMm = Number(
    settings.paperWidthMm || defaultLocalPrintProfile.receipt.paperWidthMm
  )

  return {
    printerId: settings.printerId || '',
    protocol: settings.protocol || defaultLocalPrintProfile.receipt.protocol,
    // The API accepts only 58 and 80 (as numbers or strings), so this is `Number()` of either.
    paperWidthMm: paperWidthMm === 58 ? 58 : 80,
    cut: settings.cut !== false,
    autoPrint: settings.autoPrint === true,
  }
}

function cloneDefaultProfile(): LocalPrintProfile {
  return structuredClone(defaultLocalPrintProfile)
}

function workstationQueryKey(
  organizationId: RequiredOrganizationId,
  profile: Pick<LocalPrintPairing, 'computerId' | 'machineId'>
) {
  return snapdragonQueryKeys.workstation({
    organizationId,
    computerId: profile.computerId,
    machineId: profile.machineId || undefined,
  })
}

function storedString(profile: unknown, key: keyof LocalPrintPairing) {
  const value = readProperty(profile, key)
  return typeof value === 'string' ? value : ''
}

// Pairing credentials and the signed checkout identity must survive a reload even
// when the desktop is unavailable. Printer preferences belong to the Iris query.
// The profile may be JSON read back from browser storage, so each field is checked.
function pairingFromProfile(profile: unknown): LocalPrintPairing {
  const workstationId = readProperty(profile, 'workstationId')

  return {
    token: storedString(profile, 'token'),
    computerId: storedString(profile, 'computerId'),
    machineId: storedString(profile, 'machineId'),
    stationId: storedString(profile, 'stationId'),
    stationName: storedString(profile, 'stationName'),
    workstationId: typeof workstationId === 'number' ? workstationId : null,
    workstationClaim: storedString(profile, 'workstationClaim'),
  }
}

export function loadLocalPrintProfile(
  organizationId: OrganizationIdInput,
  computerId = ''
): LocalPrintProfile {
  const defaults = cloneDefaultProfile()
  if (typeof window === 'undefined') return defaults

  try {
    const resolvedComputerId = computerId || activeComputerId(organizationId)
    const machineProfile = resolvedComputerId
      ? window.localStorage.getItem(computerStorageKey(organizationId, resolvedComputerId))
      : null
    const legacyProfile = window.localStorage.getItem(legacyStorageKey(organizationId))
    const stored: unknown = JSON.parse(machineProfile || legacyProfile || '{}')
    const storedComputerId = readProperty(stored, 'computerId')
    if (
      resolvedComputerId &&
      !machineProfile &&
      storedComputerId &&
      storedComputerId !== resolvedComputerId
    ) {
      return { ...defaults, computerId: resolvedComputerId }
    }
    const pairing = pairingFromProfile(stored)
    const profile = {
      ...defaults,
      ...pairing,
      computerId: resolvedComputerId || pairing.computerId,
    }
    // Workstation queries always carry an organization, so there's no cached response without one.
    const response =
      organizationId === null || organizationId === undefined
        ? undefined
        : queryClient.getQueryData<SnapdragonWorkstationResponse>(
            workstationQueryKey(organizationId, profile)
          )
    return profile.token
      ? mergeLocalPrintWorkstationProfile(profile, response?.workstation)
      : profile
  } catch {
    return defaults
  }
}

export function saveLocalPrintPairing(
  organizationId: OrganizationIdInput,
  profile: Partial<LocalPrintPairing>,
  { advanceRevision = true }: { advanceRevision?: boolean } = {}
): number {
  const normalizedProfile = pairingFromProfile(profile)
  const computerId = normalizedProfile.computerId.trim()
  const serializedProfile = JSON.stringify(normalizedProfile)
  if (!computerId) {
    const key = legacyStorageKey(organizationId)
    if (window.localStorage.getItem(key) === serializedProfile) {
      return currentProfileRevision(organizationId)
    }
    if (advanceRevision) advanceLocalPrintProfileRevision(organizationId)
    window.localStorage.setItem(key, serializedProfile)
    publishLocalPrintProfileUpdate(organizationId, 'saved')
    return currentProfileRevision(organizationId)
  }

  const activeKey = activeComputerStorageKey(organizationId)
  const profileKey = computerStorageKey(organizationId, computerId)
  const activeComputerChanged = window.localStorage.getItem(activeKey) !== computerId
  const profileChanged = window.localStorage.getItem(profileKey) !== serializedProfile
  if (!activeComputerChanged && !profileChanged) {
    window.localStorage.removeItem(legacyStorageKey(organizationId))
    return currentProfileRevision(organizationId)
  }

  if (advanceRevision) advanceLocalPrintProfileRevision(organizationId)
  if (activeComputerChanged) window.localStorage.setItem(activeKey, computerId)
  if (profileChanged) window.localStorage.setItem(profileKey, serializedProfile)
  window.localStorage.removeItem(legacyStorageKey(organizationId))
  publishLocalPrintProfileUpdate(organizationId, 'saved')
  return currentProfileRevision(organizationId)
}

export function clearLocalPrintProfile(
  organizationId: OrganizationIdInput,
  computerId = ''
): boolean {
  if (typeof window === 'undefined') return false

  invalidateLocalPrintProfileWrites(organizationId)
  let cleared = false

  try {
    const storedActiveComputerId = activeComputerId(organizationId)
    const resolvedComputerId = String(computerId || storedActiveComputerId).trim()
    window.localStorage.removeItem(legacyStorageKey(organizationId))
    if (resolvedComputerId) {
      window.localStorage.removeItem(computerStorageKey(organizationId, resolvedComputerId))
    }
    if (!computerId || storedActiveComputerId === resolvedComputerId) {
      window.localStorage.removeItem(activeComputerStorageKey(organizationId))
    }
    cleared = true
  } catch {
    // Disconnect still succeeds when browser storage is unavailable.
  }

  publishLocalPrintProfileUpdate(organizationId, 'cleared')
  return cleared
}

function printingSettingsFromProfile(
  profile: SnapdragonPrintingSettings | null | undefined
): SnapdragonPrintingSettings {
  const orderSummary: Partial<SnapdragonOrderSummaryOutputSettings> = profile?.orderSummary ?? {}
  const superTicket: Partial<SnapdragonSuperTicketOutputSettings> = profile?.superTicket ?? {}
  const receipt: Partial<SnapdragonReceiptOutputSettings> = profile?.receipt ?? {}

  return {
    orderSummary: {
      enabled: orderSummary.enabled !== false,
      printerId: orderSummary.printerId || '',
      copies: Number(orderSummary.copies || 1),
      tray: orderSummary.tray || '',
      autoPrint: orderSummary.autoPrint === true,
    },
    superTicket: {
      enabled: superTicket.enabled !== false,
      printerId: superTicket.printerId || '',
      copies: Number(superTicket.copies || 1),
      tray: superTicket.tray || '',
      template: superTicket.template || defaultLocalPrintProfile.superTicket.template,
      paperHasPreprintedBranding: superTicket.paperHasPreprintedBranding === true,
      autoPrint: superTicket.autoPrint === true,
    },
    receipt: normalizeLocalReceiptSettings(receipt),
  }
}

export function mergeLocalPrintWorkstationProfile(
  profile: LocalPrintProfile,
  workstation: SnapdragonWorkstation | null | undefined
): LocalPrintProfile {
  if (!workstation) return profile

  const settings: SnapdragonPrintingSettings = workstation.printing_settings ?? {}
  return {
    ...profile,
    workstationId: workstation.id ?? null,
    workstationClaim: workstation.checkout_claim ?? profile.workstationClaim ?? '',
    workstationName: workstation.name ?? '',
    locationId: workstation.location_id ?? null,
    registerLabel: workstation.register_label ?? '',
    setupCompleted: workstation.setup_completed === true,
    posPrintMode: workstation.pos_print_mode ?? 'legacy_cloud',
    settingsVersion: workstation.settings_version,
    orderSummary: { ...defaultLocalPrintProfile.orderSummary, ...settings.orderSummary },
    superTicket: { ...defaultLocalPrintProfile.superTicket, ...settings.superTicket },
    receipt: normalizeLocalReceiptSettings(settings.receipt),
  }
}

export async function syncLocalPrintWorkstation({
  organizationId,
  profile,
  station,
  shouldPersist = () => true,
}: {
  organizationId: RequiredOrganizationId
  profile: LocalPrintProfile
  station?: LocalPrintStationIdentity | null
  shouldPersist?: () => boolean
}): Promise<SnapdragonWorkstationResponse & { profile: LocalPrintProfile }> {
  const writeIsCurrent = createLocalPrintProfileWriteGuard(organizationId)
  const profileRevisionIsCurrent = createLocalPrintProfileRevisionGuard(organizationId)
  const computerId = String(profile?.computerId ?? '').trim()
  const machineId = String(profile?.machineId ?? '').trim()
  const stationId = String(station?.stationId ?? profile?.stationId ?? '').trim()
  if (!computerId || !stationId) return { profile, workstation: null, locations: [] }

  const current = await getSnapdragonWorkstation({ organizationId, computerId, machineId })
  const currentIdentityMatches =
    current?.workstation?.computer_id === computerId &&
    (!machineId || current?.workstation?.machine_id === machineId)
  let response: SnapdragonWorkstationResponse
  let workstationChanged = false
  if (current?.workstation?.station_id === stationId) {
    if (currentIdentityMatches) {
      response = current
    } else {
      response = await associateSnapdragonWorkstation({
        organizationId,
        computer_id: computerId,
        machine_id: machineId,
        station_id: stationId,
      })
      workstationChanged = true
    }
  } else {
    response = await saveSnapdragonWorkstation({
      organizationId,
      computer_id: computerId,
      ...(machineId ? { machine_id: machineId } : {}),
      station_id: stationId,
      station_name: station?.stationName ?? profile.stationName ?? null,
    })
    workstationChanged = true
  }
  const nextProfile = mergeLocalPrintWorkstationProfile(profile, response?.workstation)
  if (shouldPersist() && writeIsCurrent() && profileRevisionIsCurrent()) {
    saveLocalPrintPairing(organizationId, nextProfile, { advanceRevision: false })
    queryClient.setQueryData(workstationQueryKey(organizationId, nextProfile), response)
  }
  if (workstationChanged) {
    await invalidateSnapdragonWorkstations(organizationId)
  }

  return { ...response, profile: nextProfile }
}

export async function saveLocalPrintWorkstationProfile(
  organizationId: RequiredOrganizationId,
  profile: LocalPrintProfile,
  assignments: LocalPrintWorkstationAssignments = {}
): Promise<SnapdragonWorkstationResponse & { profile: LocalPrintProfile }> {
  const computerId = String(profile?.computerId ?? '').trim()
  const machineId = String(profile?.machineId ?? '').trim()
  const stationId = String(profile?.stationId ?? '').trim()
  if (!computerId || !stationId) {
    throw new Error('Connect this computer to Snapdragon before saving its settings.')
  }

  return serializeLocalPrintWorkstationMutation(organizationId, computerId, async () => {
    const writeIsCurrent = createLocalPrintProfileWriteGuard(organizationId)
    if (!localPrintProfilePairingIsActive(organizationId, profile)) {
      throw profileWriteInvalidatedError()
    }

    advanceLocalPrintProfileRevision(organizationId)
    const profileRevisionIsCurrent = createLocalPrintProfileRevisionGuard(organizationId)
    const queryKey = workstationQueryKey(organizationId, profile)
    await queryClient.cancelQueries({ queryKey, exact: true })
    if (!writeIsCurrent() || !localPrintProfilePairingIsActive(organizationId, profile)) {
      throw profileWriteInvalidatedError()
    }
    const workstation = {
      computer_id: computerId,
      ...(machineId ? { machine_id: machineId } : {}),
      station_id: stationId,
      station_name: profile.stationName || null,
      ...(Object.hasOwn(assignments, 'name') ? { name: assignments.name || null } : {}),
      ...(Object.hasOwn(assignments, 'locationId')
        ? { location_id: assignments.locationId || null }
        : {}),
      ...(Object.hasOwn(assignments, 'registerLabel')
        ? { register_label: assignments.registerLabel || null }
        : {}),
      ...(Object.hasOwn(assignments, 'setupCompleted')
        ? { setup_completed: assignments.setupCompleted === true }
        : {}),
      ...(Object.hasOwn(assignments, 'posPrintMode')
        ? { pos_print_mode: assignments.posPrintMode }
        : {}),
    }
    const response = await saveSnapdragonWorkstation(
      assignments.includePrintingSettings
        ? {
            organizationId,
            ...workstation,
            printing_settings: printingSettingsFromProfile(profile),
            // The API rejects a missing or empty version alike, so an unread version still fails there.
            expected_settings_version: profile.settingsVersion ?? '',
          }
        : { organizationId, ...workstation }
    )
    const nextProfile = mergeLocalPrintWorkstationProfile(profile, response?.workstation)
    if (
      !writeIsCurrent() ||
      !profileRevisionIsCurrent() ||
      !localPrintProfilePairingIsActive(organizationId, profile)
    ) {
      await invalidateSnapdragonWorkstations(organizationId)
      throw profileWriteInvalidatedError()
    }
    await queryClient.cancelQueries({ queryKey, exact: true })
    if (
      !writeIsCurrent() ||
      !profileRevisionIsCurrent() ||
      !localPrintProfilePairingIsActive(organizationId, profile)
    ) {
      await invalidateSnapdragonWorkstations(organizationId)
      throw profileWriteInvalidatedError()
    }
    saveLocalPrintPairing(organizationId, nextProfile, { advanceRevision: false })
    queryClient.setQueryData(queryKey, response)
    await invalidateSnapdragonWorkstations(organizationId)

    return { ...response, profile: nextProfile }
  })
}

export function hasConfiguredLocalPrint(
  organizationId: OrganizationIdInput,
  kind?: LocalPrintOutputKind
): boolean {
  const profile = loadLocalPrintProfile(organizationId)
  if (!profile.token || !profile.stationName) return false
  if (!kind) return true
  if (kind === 'receipt') {
    return Boolean(profile.receipt.printerId)
  }
  const outputProfile = kind === 'superTicket' ? profile.superTicket : profile.orderSummary
  return outputProfile.enabled !== false && Boolean(outputProfile.printerId)
}
